import { NavigableButton } from '@carvana/ds-button';
import { icon } from '@carvana/ds-styles';
import abbreviateMileage from '@carvana/merch-tile/utils/abbreviateMileage';
import { AlertCircle } from 'lucide-react';
import {
  BodyText,
  Card,
  ContentSection,
  Headline,
  TaskWarning,
  VehicleInfo,
  VehicleSection,
  VehicleSubText,
  VehicleTitle
} from './OrderStatusCard.styles';
import { CtaAnchorProps, OrderStatusCardConfig } from '../utils';

type OwnProps = {
  buttonCtaProps: CtaAnchorProps;
  config: OrderStatusCardConfig;
  remainingTasksCtaProps: CtaAnchorProps;
  vehicle: {
    make: string;
    mileage: number;
    model: string;
    trim: string;
    year: number;
  };
};

export default function OrderStatusCard({
  buttonCtaProps,
  config,
  remainingTasksCtaProps,
  vehicle
}: OwnProps) {
  const {
    bodyText,
    ctaText,
    headlineText,
    openVerificationsCount,
    wrapHeadline
  } = config;
  const { make, mileage, model, trim, year } = vehicle;

  return (
    <Card>
      <ContentSection>
        <Headline>
          {(() => {
            const commaIdx = wrapHeadline ? headlineText.lastIndexOf(', ') : -1;
            if (commaIdx < 0) return <span>{headlineText}</span>;

            return (
              <>
                <span>{headlineText.slice(0, commaIdx + 1)}</span>
                <span>{headlineText.slice(commaIdx + 2)}</span>
              </>
            );
          })()}
        </Headline>

        {openVerificationsCount != null && (
          <a
            className="contents"
            data-testid="order-status-remaining-tasks"
            {...remainingTasksCtaProps}
          >
            <TaskWarning>
              <AlertCircle color={icon.inverse} size={24} />
              <span className="text-white t-heading-sm">
                You have{' '}
                {openVerificationsCount === 1
                  ? '1 task'
                  : `${openVerificationsCount} tasks`}{' '}
                to complete.
              </span>
            </TaskWarning>
          </a>
        )}

        {bodyText && <BodyText>{bodyText}</BodyText>}
      </ContentSection>

      <VehicleSection>
        <VehicleInfo>
          <VehicleTitle>
            {year} {make} {model}
          </VehicleTitle>
          <VehicleSubText>
            <span>{trim}</span>
            {mileage > 0 && (
              <>
                <span
                  aria-hidden="true"
                  className="inline-block size-[3px] rounded-full bg-white"
                />
                <span>{abbreviateMileage(mileage)} miles</span>
              </>
            )}
          </VehicleSubText>
        </VehicleInfo>

        <NavigableButton
          {...buttonCtaProps}
          className="sm:w-auto sm:self-start !bg-background-default !text-text-primary hover:!bg-background-default-hover hover:!text-text-primary-hover !border-0"
          size="md"
          variant="secondary"
        >
          {ctaText}
        </NavigableButton>
      </VehicleSection>
    </Card>
  );
}
